import { MouseEventHandler, useState } from 'react';
import { Button } from 'nhsuk-react-components';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';
import useUpdateIterationStatus from 'queries/mutate/updateIterationStatus.ts';
import { canRevertToDraft } from 'utils/iterationStatusUtils.ts';
import useUserAuthorisation from 'hooks/useUserAuthorisation';
import useAuth from 'hooks/useAuth.ts';
import useCampaign from 'hooks/useCampaign.ts';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import { getIterationDetailsById } from 'utils/iterationUtils.ts';
import Confirm from 'components/Confirm';

interface IRevertToDraftProps {
  teamId: string,
  campaignId: string,
  iterationId: string,
}

function RevertToDraft({ campaignId, iterationId, teamId }: IRevertToDraftProps) {
  const { user } = useAuth();
  const { canUpdateCampaign } = useUserAuthorisation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { campaign } = useCampaign();

  const [isShowingRevertToDraftDialog, setIsShowingRevertToDraftDialog] = useState(false);
  const iterationDetails = getIterationDetailsById(campaign, iterationId as string);
  const updateIterationStatus = useUpdateIterationStatus();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId: teamId!, campaignId: campaignId! });
  const onClickRevertToDraft: MouseEventHandler = () => {
    setIsShowingRevertToDraftDialog(true);
  };

  const abort = () => {
    setIsShowingRevertToDraftDialog(false);
  };

  const confirm = async () => {
    setIsShowingRevertToDraftDialog(false);
    try {
      setIsSubmitting(true);
      await updateIterationStatus.mutateAsync({
        campaignId,
        iterationId,
        teamId,
        status: ITERATION_STATUS.DRAFT,
      });
      await fetchSpecificCampaign.refetch();
    } finally {
      setIsSubmitting(false);
    }
  };

  const canRevertToDraftResult = canRevertToDraft(
    canUpdateCampaign(),
    iterationDetails,
    user.userId,
  );
  if (!canRevertToDraftResult) {
    return null;
  }

  return (
    <>
      <Button onClick={onClickRevertToDraft} warning disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Revert to draft'}
      </Button>
      {isShowingRevertToDraftDialog && (
        <Confirm
          title="Revert to draft?"
          message="This will change the iteration back to draft so you can make changes. You’ll need to complete all the review steps again, including sending it for a run."
          yesText="Yes, revert to draft"
          noText="No, return to summary"
          onYes={confirm}
          onNo={abort}
        />
      )}
    </>
  );
}
export default RevertToDraft;
